import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, delay, distinctUntilChanged, map, Observable, of, Subscription, switchMap, from, catchError, EMPTY } from 'rxjs';

export class TriggeredTableDataSource<T> extends MatTableDataSource<T> {
  private isLoading = new BehaviorSubject<boolean>(false);
  private refreshTrigger: Subscription;
  private pendingLoad: Promise<void> = Promise.resolve();
  private queuedLoad: Promise<void> | null = null;
  protected loadGeneration = 0;

  isRefreshEnabled = false;

  get isLoading$(): Observable<boolean> {
    return this.delayFalse(this.isLoading.asObservable(), 100);
  }

  get isEmpty$(): Observable<boolean> {
    return this.isLoading$.pipe(
      distinctUntilChanged(),
      map(() => this.data.length === 0),
    );
  }

  constructor(private dataLoader: (...args: any[]) => Promise<T[]>) {
    super();
  }

  setRefreshTrigger(trigger: Observable<any>, isRefreshEnabled: boolean): void {
    this.refreshTrigger?.unsubscribe();
    this.refreshTrigger = null;
    if (trigger) {
      this.refreshTrigger = trigger
        .pipe(
          switchMap(() => from(this.refreshData())),
          catchError(error => {
            console.error('Data refresh failed:', error);
            return EMPTY;
          }),
        )
        .subscribe();
    }
    this.isRefreshEnabled = isRefreshEnabled;
  }

  clearRefreshTrigger(): void {
    this.setRefreshTrigger(null, false);
  }

  async loadData(): Promise<void> {
    // Coalesce repeated calls (e.g. rapid scrollNearEnd events): while a load is already
    // queued behind the in-flight one, reuse it instead of stacking up back-to-back fetches.
    if (this.queuedLoad) {
      return this.queuedLoad;
    }
    await this.enqueueLoad();
  }

  private enqueueLoad(): Promise<void> {
    // Serialize loads so a reload never runs concurrently with an in-flight page fetch.
    const run = this.pendingLoad.then(() => {
      this.queuedLoad = null;
      return this.runLoad();
    });
    this.queuedLoad = run.then((): void => undefined, (): void => undefined);
    this.pendingLoad = run.catch((): void => undefined);
    return run;
  }

  private async runLoad(): Promise<void> {
    if (!this.canLoadData()) {
      return;
    }

    this.isLoading.next(true);
    try {
      await this.loadDataInternal();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      this.isLoading.next(false);
    }
  }

  async reloadData(): Promise<void> {
    this.clear();
    // Reload always forces a fresh run, even if scroll events already queued a load.
    await this.enqueueLoad();
  }

  async refreshData(): Promise<void> {
    if (this.canRefreshData()) {
      await this.refreshDataInternal();
    }
  }

  clear(): void {
    this.loadGeneration++;
    this.data = [];
  }

  protected async loadDataInternal(): Promise<void> {
    const generation = this.loadGeneration;
    const data = await this.dataLoader();
    if (generation !== this.loadGeneration) {
      // A clear/reload happened while this load was in flight; discard the stale result.
      return;
    }
    this.data = data;
  }

  protected async refreshDataInternal(): Promise<void> {
    // Refresh discards any in-flight load and forces its own run (never coalesced away).
    this.loadGeneration++;
    await this.enqueueLoad();
  }

  protected canLoadData(): boolean {
    return this.isLoading.value === false;
  }

  protected canRefreshData(): boolean {
    return this.isRefreshEnabled;
  }

  private delayFalse(source$: Observable<boolean>, minDelay: number): Observable<boolean> {
    let lastTrueEmissionTime = 0;

    return source$.pipe(
      switchMap(value => {
        if (value || lastTrueEmissionTime === 0) {
          lastTrueEmissionTime = Date.now();
          return of(value);
        } else {
          const timeSinceLastTrue = Date.now() - lastTrueEmissionTime;
          const delayTime = timeSinceLastTrue >= minDelay ? 0 : minDelay - timeSinceLastTrue;
          return of(value).pipe(delay(delayTime));
        }
      }),
    );
  }
}
